import * as React from "react";
import { useMutation, useQuery } from "react-query";
import { ApiKey, ApiKeyMtlsCertificate, ApiKeysService } from "gen/clients";
import { useDialogState } from "hooks/useDialogState";
import Drawer from "@mui/material/Drawer";
import { Box, Button, Stack, Tooltip, Typography } from "@mui/material";
import { DrawerHeader } from "components/DrawerHeader/DrawerHeader";
import { DrawerBody } from "components/DrawerBody/DrawerBody";
import { DataTable } from "components/DataTable/DataTable";
import { DataTableRowAction } from "components/DataTable/types";
import { ApiErrorMessage } from "components/ApiErrorMessage/ApiErrorMessage";
import { ConfirmationDialog } from "components/ConfirmationDialog/ConfirmationDialog";
import { formatDate } from "utils/dateUtils";
import { AddMtlsCertificateDialog } from "./AddMtlsCertificateDialog";
import { EditMtlsCertificateDialog } from "./EditMtlsCertificateDialog";

interface Props {
  apiKey: ApiKey;
  open: boolean;
  onClose: () => void;
  onSaveSuccess: () => void;
}

const truncateFingerprint = (value: string) => (value.length > 16 ? `${value.slice(0, 16)}…` : value);

const MtlsCertificatesPanel: React.FC<Props> = ({ apiKey, open, onClose, onSaveSuccess }) => {
  const [isAddOpen, openAdd, closeAdd] = useDialogState();
  const [isEditOpen, openEdit, closeEdit] = useDialogState();
  const [isDeleteOpen, openDelete, closeDelete] = useDialogState();
  const [selectedCert, setSelectedCert] = React.useState<ApiKeyMtlsCertificate | undefined>();

  const {
    data: apiKeyData,
    isLoading,
    isError,
    error,
    refetch
  } = useQuery(["getApiKey", apiKey.id], {
    queryFn: () => ApiKeysService.getApiKey({ keyId: apiKey.id }),
    enabled: open,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    cacheTime: 0
  });

  const {
    mutate: deleteCertificate,
    isLoading: isDeleting,
    error: deleteError,
    reset: resetDelete
  } = useMutation(ApiKeysService.deleteApiKeyMtlsCertificate, {
    onSuccess: () => {
      closeDelete();
      refetch();
      onSaveSuccess();
    }
  });

  const onMutationSuccess = React.useCallback(() => {
    closeAdd();
    closeEdit();
    refetch();
    onSaveSuccess();
  }, [closeAdd, closeEdit, refetch, onSaveSuccess]);

  const onEditClick = React.useCallback(
    (cert: ApiKeyMtlsCertificate) => {
      setSelectedCert(cert);
      openEdit();
    },
    [openEdit]
  );

  const onDeleteClick = React.useCallback(
    (cert: ApiKeyMtlsCertificate) => {
      resetDelete();
      setSelectedCert(cert);
      openDelete();
    },
    [openDelete, resetDelete]
  );

  const onDeleteConfirm = React.useCallback(() => {
    if (selectedCert) {
      deleteCertificate({ keyId: apiKey.id, certificateId: selectedCert.id });
    }
  }, [apiKey.id, deleteCertificate, selectedCert]);

  const descriptionCell = React.useCallback((row: ApiKeyMtlsCertificate) => {
    return <Typography variant="body2">{row.description || "-"}</Typography>;
  }, []);

  const subjectDnCell = React.useCallback((row: ApiKeyMtlsCertificate) => {
    return (
      <Tooltip title={row.subjectDn}>
        <Typography variant="body2" sx={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }} noWrap={true}>
          {row.subjectDn}
        </Typography>
      </Tooltip>
    );
  }, []);

  const issuerDnCell = React.useCallback((row: ApiKeyMtlsCertificate) => {
    return (
      <Tooltip title={row.issuerDn}>
        <Typography variant="body2" sx={{ maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis" }} noWrap={true}>
          {row.issuerDn}
        </Typography>
      </Tooltip>
    );
  }, []);

  const serialNumberCell = React.useCallback((row: ApiKeyMtlsCertificate) => {
    return (
      <Typography variant="body2" fontFamily="monospace">
        {row.serialNumber}
      </Typography>
    );
  }, []);

  const fingerprintCell = React.useCallback((row: ApiKeyMtlsCertificate) => {
    return (
      <Tooltip title={row.sha256Fingerprint}>
        <Typography variant="body2" fontFamily="monospace">
          {truncateFingerprint(row.sha256Fingerprint)}
        </Typography>
      </Tooltip>
    );
  }, []);

  const notBeforeCell = React.useCallback((row: ApiKeyMtlsCertificate) => {
    return <Typography variant="body2">{formatDate(new Date(row.notBefore))}</Typography>;
  }, []);

  const notAfterCell = React.useCallback((row: ApiKeyMtlsCertificate) => {
    return <Typography variant="body2">{formatDate(new Date(row.notAfter))}</Typography>;
  }, []);

  const statusCell = React.useCallback((row: ApiKeyMtlsCertificate) => {
    return (
      <Typography variant="body2" color={row.enabled ? "success.main" : "error.main"}>
        {row.enabled ? "Active" : "Inactive"}
      </Typography>
    );
  }, []);

  const rowActions = React.useCallback(
    (cert: ApiKeyMtlsCertificate): DataTableRowAction[] => [
      { title: "Edit", action: () => onEditClick(cert) },
      { title: "Delete", action: () => onDeleteClick(cert), color: "error.main" }
    ],
    [onEditClick, onDeleteClick]
  );

  const certificates = apiKeyData?.mtls?.allowedCertificates || [];

  return (
    <>
      <Drawer open={open} onClose={onClose} anchor="right" PaperProps={{ sx: { width: "80vw" } }}>
        <DrawerHeader title={`Manage Certificates — ${apiKey.name}`} onClose={onClose} />
        <DrawerBody>
          <Stack spacing={2}>
            <Box>
              <Button variant="contained" color="primary" onClick={openAdd}>
                Add Certificate
              </Button>
            </Box>
            <DataTable
              columns={[
                { id: "description", title: <strong>Description</strong>, cell: descriptionCell },
                { id: "subjectDn", title: <strong>Subject DN</strong>, cell: subjectDnCell },
                { id: "issuerDn", title: <strong>Issuer DN</strong>, cell: issuerDnCell },
                { id: "serialNumber", title: <strong>Serial</strong>, cell: serialNumberCell },
                { id: "sha256Fingerprint", title: <strong>SHA-256</strong>, cell: fingerprintCell },
                { id: "notBefore", title: <strong>Not Before</strong>, cell: notBeforeCell },
                { id: "notAfter", title: <strong>Not After</strong>, cell: notAfterCell },
                { id: "status", title: <strong>Status</strong>, cell: statusCell }
              ]}
              data={certificates}
              rowKey={(row: ApiKeyMtlsCertificate) => row.id}
              rowActions={rowActions}
              isLoading={isLoading}
              error={isError && error ? <ApiErrorMessage apiError={error} /> : undefined}
              noData="No certificates configured"
            />
          </Stack>
        </DrawerBody>
      </Drawer>

      {isAddOpen && <AddMtlsCertificateDialog keyId={apiKey.id} onClose={closeAdd} onSuccess={onMutationSuccess} />}

      {isEditOpen && selectedCert && (
        <EditMtlsCertificateDialog
          keyId={apiKey.id}
          certificate={selectedCert}
          onClose={closeEdit}
          onSuccess={onMutationSuccess}
        />
      )}

      {isDeleteOpen && selectedCert && (
        <ConfirmationDialog
          title="Delete Certificate"
          content={
            <div>
              Are you sure you want to delete this certificate binding
              {selectedCert.description ? (
                <>
                  {" "}
                  (<strong>{selectedCert.description}</strong>)
                </>
              ) : null}
              ? This action cannot be undone.
            </div>
          }
          onClose={closeDelete}
          onConfirm={onDeleteConfirm}
          confirmationButtonColor="error"
          isLoading={isDeleting}
          error={deleteError ? <ApiErrorMessage apiError={deleteError} /> : undefined}
        />
      )}
    </>
  );
};

export { MtlsCertificatesPanel };
